import FilterContainer from '@/components/filters/FilterContainer.jsx';
import { FiFilter } from '@/components/react-icons/main';
import DividerV2 from '@/components/v2/DividerV2.jsx';
import useAsyncArgumentValue from '@/hooks/useAsyncArgumentValue.js';
import useRunStepAction from '@/hooks/useRunStepAction.js';
import { action as reduxAction } from '@/lib/api';
import { getFieldsFromSchemaPath, getGraphqlFilterIntrospection, introspectionQuery } from '@/lib/graphql.js';
import { getDeepByPath } from '@shared/utils/getDeepByPath.js';
import normalizeToArray from '@/lib/normalizeToArray.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import selectors from '@/redux/selectors/selectors.js';
import argumentDataSources from '@/schema/argumentDataSources.js';
import appActions from '@/screens/apps/redux/actions/appActions.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { camelToTitle } from '@shared/utils/camelToTitle.js';
import { randomId } from '@shared/utils/randomId.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import getFieldOptions from '../argumentsOptionsWrapper/getFieldOptions.js';
import getFilterOperatorOptions from '../argumentsOptionsWrapper/getFilterOperatorOptions.js';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';

// Decides which field options FilterSlider should feed into FilterContainer.
// - GraphQL introspection: use the introspected field list.
// - Transform / post-processing slider: use the prior step's response-schema attributes.
// - Otherwise (regular action filter slider): return undefined so FilterContainer
//   falls back to its useLookup() result, which surfaces the action's configured
//   filter fields (action.features.filters.fields). Passing an array here would
//   suppress that lookup and incorrectly show response-schema fields instead.
const resolveFilterSliderFieldSource = ({ useIntrospectionFields, isTransformAction, isTransformApp, graphqlFieldOptions, fieldOptions }) => {
  if (useIntrospectionFields) {
    return graphqlFieldOptions;
  }
  if (isTransformAction || isTransformApp) {
    return fieldOptions;
  }
  return undefined;
};

const normalizeConjunctions = (filters) => {
  if (!filters || typeof filters !== 'object') return { filterSet: [], conjunction: 'and' };
  const clonedFilters = JSON.parse(JSON.stringify(filters));

  const processFilterSet = (filterObj) => {
    if (!filterObj) return;
    if (filterObj.filterSet && filterObj.filterSet.length === 1) {
      filterObj.conjunction = 'and';
    }

    if (filterObj.filterSet) {
      filterObj.filterSet.forEach((filter) => {
        if (filter.type === 'nested' && filter.filterSet) {
          processFilterSet(filter);
        }
      });
    }
  };

  processFilterSet(clonedFilters);
  return clonedFilters;
};

const FilterSlider = ({ onClose, dataSource, dataSourcePath, dataSavePath, lookupActionId, isTransformAction, isTransformApp, isPostProcessing, disabled = false }) => {
  const [argKey] = useState(randomId());
  const apps = useSelector(selectors.appsVersions);
  const steps = useSelector(workflowScreenSelectors.activeWorkflow)?.steps;
  const appId = useArgSelector(selectors.param, 'appId');
  const stepId = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.id);
  const appSlug = useSelector((state) => argumentSelectors[dataSource].activeApp(state)?.slug);
  const api = useSelector(argumentSelectors[dataSource].activeAppApi);
  const action = useArgSelector(argumentSelectors[dataSource].activeAction({ lookupActionId }), lookupActionId);
  const options = useSelector(argumentSelectors[dataSource].activeOptions);
  const args = useSelector(argumentSelectors[dataSource].activeArguments());
  const credentialId = useSelector(argumentSelectors[dataSource].activeCredentialId);
  const stepOutputs = useSelector(selectors.cookedWorkflowStepOutputs) || [];
  const filterOptions = (isTransformAction ? options?.postProcessing?.filters : isTransformApp ? options?.filters || options?.postProcessing?.filters : options?.filters) || [];
  const advancedFilters = (isTransformAction ? options?.postProcessing?.advancedFilters : isTransformApp ? options?.advancedFilters || options?.postProcessing?.advancedFilters : options?.advancedFilters) || { filterSet: [], conjunction: 'and' };
  const normalizedFilterOptions = normalizeToArray(filterOptions);
  // recordType
  const recordTypeArgDef = action?.arguments?.find((n) => n.key === 'recordType');
  const recordType = useAsyncArgumentValue({ arg: args?.recordType, def: recordTypeArgDef, responses: stepOutputs });
  // isCustomRecord
  const isCustomRecordArgDef = action?.arguments?.find((n) => n.key === 'isCustomRecord');
  const isCustomRecord = useAsyncArgumentValue({ arg: args?.isCustomRecord, def: isCustomRecordArgDef, responses: stepOutputs });

  const description = isTransformAction || isTransformApp ? 'Qualify the number of results returned from the action.' : 'Set conditions to retrieve only relevant results.';
  const actions = dataSource === argumentDataSources.workflowStep ? stepActions : appActions;
  const apiFilters = api?.features?.filters;
  const actionFilters = action?.features?.filters;
  const isGraphql = api?.isGraphql === true || action?.isGraphql === true;
  const useIntrospectionFields = isGraphql && (api?.graphqlSettings?.useIntrospectionFields === true || actionFilters?.useIntrospectionFields === true);
  const useIntrospectionOperators = isGraphql && (api?.graphqlSettings?.useIntrospectionOperators === true || actionFilters?.useIntrospectionOperators === true);
  const graphqlRoot = action?.graphqlRoot;
  const queryAction = api?.graphqlSettings?.queryAction || action?.queryAction;
  const graphqlSchema = useSelector((state) => state?.workflow?.graphqlSchema?.[`${credentialId}-${queryAction}`]);
  const runStepAction = useRunStepAction(dataSource);
  const isAdvanced = apiFilters?.isAdvanced || isTransformAction || isTransformApp;
  const actualFilters = !isAdvanced ? { filterSet: normalizedFilterOptions, conjunction: 'and' } : advancedFilters;
  const fieldActionIdMap = {
    [argumentDataSources.appLongPollingActionInstance]: action?.longPolling?.actionId,
    [argumentDataSources.appLongPollingExtraActionInstance]: action?.longPolling?.extraAction?.id,
    [argumentDataSources.appActionOnCompletionHookActionInstance]: action?.hooks?.onCompletion?.actionId
  };

  const fieldOptions = getFieldOptions({ actionId: fieldActionIdMap[dataSource], stepId, apps, steps, appId });

  const lookupSettings = useMemo(() => {
    if (isTransformAction || isTransformApp) {
      return;
    }

    if (appSlug === 'netsuite' && isCustomRecord) {
      return {
        ...actionFilters,
        actionInstance: {
          arguments: {
            recordType: { value: recordType },
            tableName: { value: recordType },
            requiresRecordType: { value: false }
          }
        }
      };
    }

    return actionFilters;
  }, [isTransformAction, isTransformApp, appSlug, isCustomRecord, actionFilters, recordType]);

  const graphqlFilterIntrospection = useMemo(() => {
    if ((!useIntrospectionFields && !useIntrospectionOperators) || !graphqlSchema?.schema || !graphqlRoot) {
      return null;
    }

    return getGraphqlFilterIntrospection(graphqlSchema.schema, {
      graphqlRoot,
      preferredArgName: action?.graphqlSettings?.filtering?.argName,
      segmentPath: action?.graphqlSettings?.filtering?.segment || action?.graphqlSettings?.pagination?.segment
    });
  }, [useIntrospectionFields, useIntrospectionOperators, graphqlSchema, graphqlRoot, action?.graphqlSettings?.filtering?.argName, action?.graphqlSettings?.filtering?.segment, action?.graphqlSettings?.pagination?.segment]);

  const fetchGraphqlSchema = useCallback(async () => {
    if ((!useIntrospectionFields && !useIntrospectionOperators) || !queryAction || !credentialId) {
      return;
    }

    const response = await runStepAction(queryAction, {
      arguments: {
        query: { value: introspectionQuery }
      }
    });

    const simpleSchema = response?.[0];
    const schema = getDeepByPath('Schema', simpleSchema);

    if (schema && schema.subscriptionType && Object.keys(schema.subscriptionType).length === 0) {
      delete schema.subscriptionType;
    }

    if (schema) {
      reduxAction('SET_GRAPHQL_SCHEMA', { credentialId, queryAction, schema });
    }
  }, [useIntrospectionFields, useIntrospectionOperators, queryAction, credentialId, runStepAction]);

  useEffect(() => {
    if ((useIntrospectionFields || useIntrospectionOperators) && !graphqlSchema && credentialId && queryAction) {
      fetchGraphqlSchema();
    }
  }, [useIntrospectionFields, useIntrospectionOperators, graphqlSchema, credentialId, queryAction, fetchGraphqlSchema]);

  const graphqlFieldOptions = useMemo(() => {
    if (!useIntrospectionFields || !graphqlSchema?.schema || !graphqlRoot) {
      return [];
    }
    const rawFields = getFieldsFromSchemaPath(graphqlSchema.schema, graphqlRoot);
    if (!rawFields) {
      return [];
    }
    return Object.entries(rawFields)
      .filter(([, definition]) => !definition?.fields)
      .map(([fieldName]) => ({
        label: camelToTitle(fieldName),
        value: fieldName
      }));
  }, [useIntrospectionFields, graphqlSchema, graphqlRoot]);

  const operatorOptions = useMemo(() => {
    return getFilterOperatorOptions({
      actionFilters,
      apiFilters,
      introspectionOperatorOptions: useIntrospectionOperators ? graphqlFilterIntrospection?.operatorOptions : null,
      isTransformAction,
      isTransformApp
    });
  }, [actionFilters, apiFilters, useIntrospectionOperators, graphqlFilterIntrospection, isTransformAction, isTransformApp]);

  const finalFieldOptions = resolveFilterSliderFieldSource({ useIntrospectionFields, isTransformAction, isTransformApp, graphqlFieldOptions, fieldOptions });

  const computedDataSavePath = dataSavePath ? `${dataSavePath}.options${isPostProcessing ? '.postProcessing' : ''}` : dataSourcePath;

  const handleFiltersChange = (newFilters) => {
    let processedFilters = newFilters;

    if (isAdvanced) {
      processedFilters = normalizeConjunctions(newFilters);
    }

    actions.set(`${computedDataSavePath}.${isAdvanced ? 'advancedFilters' : 'filters'}`, !isAdvanced ? processedFilters?.filterSet : processedFilters);
  };

  const handleDeleteAll = () => {
    actions.set(`${computedDataSavePath}.filters`, []);
    actions.set(`${computedDataSavePath}.advancedFilters`, { filterSet: [], conjunction: 'and' });
    onClose();
  };

  return (
    <>
      <SliderHeader onClose={onClose} showDelete showRun onDelete={handleDeleteAll} disabled={disabled} />
      <SliderTitle //
        icon={FiFilter}
        title="Filter Your Results"
        description={description}
        helpIconContext={isTransformAction || isTransformApp ? 'transformPanelFilterContext' : 'workflowStepPostFilterContext'}
      />
      <FilterContainer //
        key={argKey}
        disabled={disabled}
        dataSource={dataSource}
        isTransformAction={isTransformAction || isTransformApp}
        lookupSettings={lookupSettings}
        filters={actualFilters}
        fieldOptions={finalFieldOptions}
        operatorOptions={operatorOptions}
        isAdvanced={isAdvanced}
        customOperators={apiFilters?.useCustomOperators}
        useDynamicFields={!!actionFilters?.useDynamicFields}
        onChange={handleFiltersChange}
      />
      <DividerV2 />
      <SliderClose onClose={onClose} />
    </>
  );
};

export { normalizeConjunctions, resolveFilterSliderFieldSource };
export default FilterSlider;
